import React from 'react'
import styled, { css, StyledProps } from 'styled-components'
import { helpersColor } from '../../../helpers'
import { IconArrowBack, IconLink } from '../../iconography/Icons/Icons'

const Wrapper = styled.div`
  background-color: ${props => props.theme.color.onPrimaryLight};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  display: flex;
  flex-direction: column;

  ${(props: StyledProps<{ noDivider?: boolean }>) =>
    !props.noDivider &&
    css`
      & > div:not(:last-child) {
        border-bottom: 1px solid ${props => helpersColor.alphaColor(props.theme.color.secondary200, 0.24)};
      }
    `}

  ${props => props.theme.media.mobile`
    box-shadow: none;
    border-radius: 0;
  `}
`

const BackButton = styled(IconArrowBack)`
  margin-right: 24px;
  cursor: pointer;
  transition: 0.2s fill ease-out;

  &:hover {
    fill: ${({ theme }) => theme.color.primary600};
  }
`

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.color.primary600};

  & > a {
    color: ${props => props.theme.color.primary600};
  }
`

const LinkIconStyled = styled(IconLink)`
  margin-right: 6px;
`

const TitleContainer = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  min-height: 72px;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 24px;
  border-bottom: 1px solid ${props => props.theme.color.secondary50};

  ${props => props.theme.media.desktop`
    padding: 0 12px 0 24px;
    min-height: 52px;
  `}
  ${props => props.theme.media.tablet`
    padding: 0 12px 0 24px;
    min-height: 52px;
  `}
  ${props => props.theme.media.mobile`
    padding: 0 12px 0 16px;
    min-height: 48px;
  `}
`

const TitleInner = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

// exportable

export const SectionTitle: React.FC<{
  onBack?: () => void
  backButton?: boolean
  onClick?: () => void
  className?: string
}> = ({ onBack, onClick, backButton = true, className, children }) => {
  return (
    <TitleContainer onClick={onClick} className={className}>
      {onBack && backButton ? <BackButton onClick={onBack} /> : null}
      <TitleInner>{children}</TitleInner>
    </TitleContainer>
  )
}

type BodyProps = StyledProps<{ paddedOnMobile?: boolean; noGrid?: boolean }>
export const SectionBody = styled.div`
  padding: 16px 24px 24px;

  ${(props: BodyProps) =>
    !props.noGrid &&
    css`
      display: grid;
      grid-gap: 32px;
    `}

  ${props => props.theme.media.desktop`
    padding: 16px 24px 24px;
  `}
  ${props => props.theme.media.tablet`
    padding: 16px 24px 24px;
  `}
  ${props => props.theme.media.mobile`
    padding: 12px ${(props: BodyProps) => (props.paddedOnMobile ? props.theme.padding.mobile.content : 0)}px 24px;
  `}
`

export const SectionFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 64px;
  padding: 0 24px;
`

export const SectionLinkWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <LinkWrapper>
      <LinkIconStyled />
      {children}
    </LinkWrapper>
  )
}

export const SectionContainer: React.FC<{ className?: string; noDivider?: boolean; children?: React.ReactNode }> = ({
  children,
  className,
  noDivider,
}) => {
  return (
    <Wrapper className={className} noDivider={noDivider}>
      {children}
    </Wrapper>
  )
}
